/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { useDetailsBooking, useLatestRef, useNavigateTo } from 'lib/hooks';
import { toDate } from 'lib/utils';
import noop from 'lodash/noop';
import { createContext, FC, useContext, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { ContextValue, LatestRef, State } from './lib/types';
import { getPrimaryErrorCode } from './lib/getPrimaryErrorCode';
import { useAmendmentErrorFlow } from './lib/useAmendmentErrorFlow';
import { useAmendmentConfirmedFlow } from './lib/useAmendmentConfirmedFlow';
import { useSyncUrlWithState } from './lib/useSyncUrlWithState';
import { createQuote } from './lib/createQuote';
import { acceptQuote } from './lib/acceptQuote';

const Context = createContext<ContextValue>({
  startDate: new Date(),
  endDate: new Date(),
  stage: 'EDIT',
  handleQuote: noop,
  handleAccept: noop,
  handleBackToEdit: noop,
});

export const ChangeDatesProvider: FC = ({ children }) => {
  const { booking } = useDetailsBooking();
  const navigateTo = useNavigateTo();

  const [state, setState] = useState<State>(() => ({
    stage: 'EDIT',
    startDate: toDate(booking.reservation.checkIn),
    endDate: toDate(booking.reservation.checkOut),
  }));

  const ref = useLatestRef<LatestRef['current']>({
    booking,
    state,
    setState,
    navigateTo,
    acceptResult: { loading: false },
    quoteResult: { loading: false },
  });

  const [quoteResult, handleQuote] = useAsyncFn(createQuote.bind(null, ref), [booking.id, state.startDate, state.endDate]);
  ref.current.quoteResult = quoteResult;

  const amendmentId = quoteResult.loading ? undefined : quoteResult.value?.id;

  const [acceptResult, handleAccept] = useAsyncFn(acceptQuote.bind(null, ref), [booking.id, amendmentId, state.startDate, state.endDate]);
  ref.current.acceptResult = acceptResult;

  const handleBackToEdit = useCallback(() => {
    ref.current.setState((state) => ({ ...state, stage: 'EDIT' }));
  }, [ref]);

  const amendment = acceptResult.value ?? quoteResult.value;
  ref.current.amendment = amendment;

  const errorCode = useMemo(getPrimaryErrorCode.bind(null, ref), [amendment, quoteResult, acceptResult]);
  ref.current.errorCode = errorCode;

  useSyncUrlWithState(ref);
  useAmendmentErrorFlow(ref);
  useAmendmentConfirmedFlow(ref);

  return (
    <Context.Provider
      value={{
        amendment,
        errorCode,
        handleQuote,
        handleAccept,
        handleBackToEdit,
        ...state,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useChangeDates = () => useContext(Context);
